import { Component, inject, ChangeDetectionStrategy } from '@angular/core';
import { DialogService } from '../../../core/services/dialog.service';

interface DialogTypeConfig {
  iconPath: string;
  iconBg: string;
  iconColor: string;
  primaryBg: string;
}

@Component({
  selector: 'app-dialog',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Eager,
  template: `
    @if (dialogService.config()) {
      <!-- Backdrop -->
      <div
        class="fixed inset-0 z-[200] flex items-center justify-center p-4"
        style="background:rgba(0,0,0,0.45);backdrop-filter:blur(4px)"
        (click)="onBackdropClick($event)"
      >
        <!-- Panel -->
        <div
          class="relative w-full max-w-md rounded-2xl shadow-2xl overflow-hidden"
          style="background:var(--card-bg);border:1px solid var(--card-border)"
          (click)="$event.stopPropagation()"
          [style.animation]="'dialogIn 0.22s cubic-bezier(0.34,1.56,0.64,1) both'"
        >
          <!-- Close button -->
          <button
            class="absolute top-3.5 right-3.5 w-7 h-7 rounded-lg flex items-center justify-center transition-all hover:opacity-70"
            style="background:var(--border-light);color:var(--text-muted)"
            (click)="closeDialog()"
          >
            <svg
              class="w-3.5 h-3.5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              stroke-width="2.5"
            >
              <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          <!-- Icon header -->
          <div class="px-6 pt-7 pb-4 flex flex-col items-center text-center">
            <div
              class="w-14 h-14 rounded-2xl flex items-center justify-center mb-4"
              [style.background]="typeConfig().iconBg"
            >
              <svg
                class="w-7 h-7"
                [style.color]="typeConfig().iconColor"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                stroke-width="2"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  [attr.d]="typeConfig().iconPath"
                />
              </svg>
            </div>

            <h3 class="text-lg font-bold mb-1.5" style="color:var(--text-primary)">
              {{ dialogService.config()!.title }}
            </h3>

            @if (dialogService.config()!.message) {
              <p class="text-sm leading-relaxed" style="color:var(--text-secondary)">
                {{ dialogService.config()!.message }}
              </p>
            }

            @if (dialogService.config()!.detail) {
              <p
                class="text-xs mt-2 px-4 py-2 rounded-lg w-full text-left"
                style="background:var(--border-light);color:var(--text-muted)"
              >
                {{ dialogService.config()!.detail }}
              </p>
            }
          </div>

          <!-- Divider -->
          <div class="h-px mx-6" style="background:var(--card-border)"></div>

          <!-- Actions -->
          <div
            class="px-6 py-4 flex gap-3"
            [class.flex-row-reverse]="!!dialogService.config()!.secondaryLabel"
          >
            <!-- Primary button -->
            <button
              class="flex-1 py-2.5 rounded-xl text-sm font-semibold text-white transition-all hover:opacity-90 active:scale-[0.98] cursor-pointer"
              [style.background]="typeConfig().primaryBg"
              (click)="onPrimary()"
            >
              {{ dialogService.config()!.primaryLabel || 'OK' }}
            </button>
            <!-- Secondary button -->
            @if (dialogService.config()!.secondaryLabel) {
              <button
                type="button"
                class="flex-1 py-2.5 rounded-xl text-sm font-semibold border transition-all hover:brightness-95 active:scale-[0.98] cursor-pointer"
                style="background: color-mix(in srgb, var(--text-muted) 10%, var(--card-bg)); color: var(--text-primary); border-color: var(--card-border)"
                (click)="onSecondary()"
              >
                {{ dialogService.config()!.secondaryLabel }}
              </button>
            }
          </div>
        </div>
      </div>

      <style>
        @keyframes dialogIn {
          from {
            opacity: 0;
            transform: scale(0.92) translateY(12px);
          }
          to {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }
      </style>
    }
  `,
})
export class DialogComponent {
  readonly dialogService = inject(DialogService);

  typeConfig(): DialogTypeConfig {
    const type = this.dialogService.config()?.type ?? 'info';
    switch (type) {
      case 'success':
        return {
          iconPath: 'M9 12l2 2 4-4m6 2a9 9 0 1 1-18 0 9 9 0 0 1 18 0z',
          iconBg: 'rgba(16,185,129,0.12)',
          iconColor: '#10b981',
          primaryBg: '#10b981',
        };
      case 'warning':
        return {
          iconPath:
            'M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008z',
          iconBg: 'rgba(245,158,11,0.12)',
          iconColor: '#f59e0b',
          primaryBg: '#f59e0b',
        };
      case 'danger':
        return {
          iconPath: 'M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008z',
          iconBg: 'rgba(239,68,68,0.12)',
          iconColor: '#ef4444',
          primaryBg: '#ef4444',
        };
      case 'confirm':
        return {
          iconPath:
            'M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0z',
          iconBg: 'rgba(99,102,241,0.12)',
          iconColor: '#6366f1',
          primaryBg: '#6366f1',
        };
      default: // info
        return {
          iconPath:
            'M11.25 11.25l.041-.02a.75.75 0 0 1 1.063.852l-.708 2.836a.75.75 0 0 0 1.063.853l.041-.021M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9-3.75h.008v.008H12V8.25z',
          iconBg: 'rgba(59,125,216,0.12)',
          iconColor: 'var(--primary)',
          primaryBg: 'var(--primary)',
        };
    }
  }

  onPrimary(): void {
    const cfg = this.dialogService.config();
    if (cfg?.onPrimary) cfg.onPrimary();
    this.dialogService.close();
  }

  onSecondary(): void {
    const cfg = this.dialogService.config();
    if (cfg?.onSecondary) cfg.onSecondary();
    this.dialogService.close();
  }

  closeDialog(): void {
    const cfg = this.dialogService.config();
    if (cfg?.onClose) cfg.onClose();
    this.dialogService.close();
  }

  onBackdropClick(e: MouseEvent): void {
    this.closeDialog();
  }
}
